import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { AtlFileUploadProps } from './file-upload.props.ts';

import * as TagNames from './file-upload.tagnames.ts';
import { enumAttributeConverter } from '../enum-types.ts';
import { getGlobalStyleSheets } from '../../global-styles.ts';
import { AtlFileUploadEvent } from './file-upload.events.ts';
import { ButtonSize, ButtonVariant } from '../button/button.enums.ts';
import { IClassMap } from '../helpers.ts';
import { classMap } from 'lit/directives/class-map.js';

const DEFAULT_FILE_UPLOAD_LABEL = 'File';
const DEFAULT_FOLDER_UPLOAD_LABEL = 'Folder';

@customElement(TagNames.FileUploadInput)
export class AtlFileUploadInput extends LitElement implements AtlFileUploadProps {
    declare toggleLabel?: string;
    declare allowedFileExtensions?: string[];
    declare foldersAllowed?: boolean;
    declare variant?: ButtonVariant;
    declare size?: ButtonSize;
    declare disabled?: boolean;
    declare selectMultiple?: boolean;
    declare fileUploadLabel?: string;
    declare folderUploadLabel?: string;

    static properties = {
        toggleLabel: { type: String },
        allowedFileExtensions: { type: Array },
        foldersAllowed: { type: Boolean },
        variant: { type: String, converter: enumAttributeConverter(ButtonVariant) },
        size: { type: String, converter: enumAttributeConverter(ButtonSize) },
        disabled: { type: Boolean },
        selectMultiple: { type: Boolean },
        fileUploadLabel: { type: String },
        folderUploadLabel: { type: String }
    }

    static styles = [
        ...getGlobalStyleSheets(),
        css`
        .icon {
           width: .75em;
           margin-right: 1em;
        }`
    ];

    protected _handleFileChange(event: Event) {
        const input = event.target as HTMLInputElement;
        const files = input.files;

        if (!files || files.length === 0) return;

        this.dispatchEvent(new AtlFileUploadEvent(files));

        // allows event to be fired again with same selections
        input.value = '';
    }
    
    private _getAllowedFileTypesString(): string {
        if (!this.allowedFileExtensions || this.allowedFileExtensions.length === 0) {
            return '';
        }
        return this.allowedFileExtensions.join(',');
    }

    _renderUploadDropdown() {
        const fileInputId = "dropdown-file-upload";
        const folderInputId = "dropdown-folder-upload";

        return html`
            <atlantis-dropdown ?disabled=${this.disabled} toggle-label=${this.toggleLabel ?? 'Upload'} variant=${this.variant ?? ButtonVariant.Secondary} size=${this.size ?? ButtonSize.Medium}>
                <atlantis-dropdown-item @click=${() => this._openInputDialog(fileInputId)}>
                    <div class="d-flex align-items-center">
                       <div class="icon">
                            <i class="fa-regular fa-file"></i>
                        </div>
                        ${this.fileUploadLabel ?? DEFAULT_FILE_UPLOAD_LABEL}
                        ${this._renderInput(false, fileInputId)}
                    </div>
                </atlantis-dropdown-item>

                <atlantis-dropdown-item @click=${() => this._openInputDialog(folderInputId)}>
                    <div class="d-flex align-items-center">
                        <div class="icon">
                            <i class="fa-regular fa-folder"></i>
                        </div>
                        ${this.folderUploadLabel ?? DEFAULT_FOLDER_UPLOAD_LABEL}
                        ${this._renderInput(true, folderInputId)}
                    </div>
                </atlantis-dropdown-item>
            </atlantis-dropdown>
        `
    }

    _openInputDialog(inputId: string) {
        const input = this.shadowRoot?.getElementById(inputId) as HTMLInputElement;
        if (!input) {
            console.error(`Could not find input with id ${inputId}`);
            return;
        }

        input.click();
    }

    _renderInput(forFolders: boolean, id: string) {
        return html`
            <input
                id=${id}
                type="file"
                @change=${this._handleFileChange}
                ?multiple=${this.selectMultiple}
                ?disabled=${this.disabled}
                accept="${this._getAllowedFileTypesString()}"
                ?webkitdirectory=${forFolders}
                hidden />`;
    }

    _renderUploadButton() {
        // need to make the label seems like a button for the file input

        this.variant ??= ButtonVariant.Secondary;
        this.size ??= ButtonSize.Medium;

        const buttonVariantClasses: IClassMap = {
            "btn": true,
            "mb-0": true,
            "btn-secondary": this.variant === ButtonVariant.Secondary,
            "btn-primary": this.variant === ButtonVariant.Cta,
            "btn-link":
                this.variant === ButtonVariant.Action ||
                this.variant === ButtonVariant.DestructiveAction,
            "btn-danger": this.variant === ButtonVariant.Destructive,
            "destructive": this.variant === ButtonVariant.DestructiveAction,
            "btn-light": this.variant === ButtonVariant.Light,
        };

        const buttonSizeClasses: IClassMap = {
            "btn-sm": this.size === ButtonSize.Small,
            "btn-lg": this.size === ButtonSize.Large,
        }

        return html`
            <label
                role="button"
                class="${classMap({
                    ...buttonVariantClasses,
                    ...buttonSizeClasses,
                    'disabled': this.disabled ?? false,
                })}"
                style=${this.disabled ? 'pointer-events: none; opacity: 0.65;' : ''}>
                ${this.toggleLabel ?? 'Upload'}
                ${this._renderInput(false, "upload-button")}
            </label>
        `;
    }

    render() {
        if (this.foldersAllowed) {
            return this._renderUploadDropdown();
        }

        return this._renderUploadButton();
    }
}
